import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import HeaderSidebarToggle from "../../Components/Common/HeaderSidebarToggle";
import { createSubscriptionPayment, getPlanDetail } from "../../helpers/backend_helper";
import { sanitizeHtml } from "../../helpers/sanitizeHtml";

interface PlanDetail {
    id: number;
    name: string;
    slug: string;
    description?: string | null;
    monthly_price?: number | null;
    plan_price_id?: number | null;
    prices?: Array<{
        id: number;
        duration_in_months: number;
        price: number;
        monthly_price?: number;
    }>;
}

const formatCurrency = (value: number): string => `${new Intl.NumberFormat("vi-VN").format(value)}đ`;

const SubscriptionDetailPage = () => {
    const navigate = useNavigate();
    const { planId } = useParams();

    const [plan, setPlan] = useState<PlanDetail | null>(null);
    const [selectedPlanPriceId, setSelectedPlanPriceId] = useState<number | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>("");
    const [isPaying, setIsPaying] = useState<boolean>(false);

    useEffect(() => {
        if (!planId) {
            setError("Không tìm thấy gói dịch vụ.");
            return;
        }

        const fetchPlanDetail = async () => {
            setLoading(true);
            setError("");

            try {
                const response: any = await getPlanDetail(planId);
                const payload = response?.data?.data ?? response?.data ?? null;
                setPlan(payload);

                const availablePrices = Array.isArray(payload?.prices) ? payload.prices : [];
                if (availablePrices.length > 0) {
                    setSelectedPlanPriceId(Number(availablePrices[0].id));
                } else {
                    setSelectedPlanPriceId(payload?.plan_price_id ? Number(payload.plan_price_id) : null);
                }
            } catch (err: any) {
                setError(err?.message || "Không thể tải chi tiết gói.");
            } finally {
                setLoading(false);
            }
        };

        fetchPlanDetail();
    }, [planId]);

    const handlePayment = async () => {
        if (!selectedPlanPriceId) {
            setError("Không tìm thấy gói giá để thanh toán.");
            return;
        }

        setIsPaying(true);
        setError("");

        try {
            const response: any = await createSubscriptionPayment({ plan_price_id: selectedPlanPriceId });
            const payload = response?.data?.data ?? response?.data ?? {};
            const checkoutUrl = payload?.checkoutUrl;
            const paymentTransactionId = payload?.payment_transaction_id ?? payload?.paymentTransactionId;

            if (checkoutUrl && paymentTransactionId) {
                navigate(`/subscription/checkout/${paymentTransactionId}`, {
                    state: {
                        checkoutUrl,
                        paymentTransactionId,
                    },
                });
                return;
            }

            setError("Không tạo được link thanh toán.");
        } catch (err: any) {
            setError(err?.message || "Không thể tạo link thanh toán.");
        } finally {
            setIsPaying(false);
        }
    };

    const selectedPrice = useMemo(() => {
        const availablePrices = plan?.prices ?? [];
        if (availablePrices.length > 0) {
            return availablePrices.find((item) => Number(item.id) === Number(selectedPlanPriceId)) ?? null;
        }

        if (plan?.plan_price_id) {
            return {
                id: plan.plan_price_id,
                duration_in_months: 1,
                price: (plan.monthly_price ?? 0),
                monthly_price: plan.monthly_price ?? 0,
            };
        }

        return null;
    }, [plan, selectedPlanPriceId]);

    const total = useMemo(() => Number(selectedPrice?.price ?? 0), [selectedPrice]);

    return (
        <React.Fragment>
            <div className="header-area" id="headerArea">
                <div className="container h-100 d-flex text-center align-items-center justify-content-between rtl-flex-d-row-r">
                    <div className="back-button me-2">
                        <button
                            type="button"
                            className="btn btn-link p-0 text-dark"
                            onClick={() => navigate("/subscription")}
                            aria-label="Quay lại"
                        >
                            <i className="mdi mdi-arrow-left"></i>
                        </button>
                    </div>

                    <div className="page-heading">
                        <h6 className="mb-0">Chi tiết gói</h6>
                    </div>

                    <HeaderSidebarToggle className="ms-2" />
                </div>
            </div>

            <div className="page-content-wrapper">
                <div className="container py-3">
                    {loading && <p className="text-center mb-0">Đang tải chi tiết gói...</p>}
                    {!loading && error && <p className="text-danger text-center mb-0">{error}</p>}

                    {!loading && !error && plan && (
                        <div className="card">
                            <div className="card-body">
                                <h5 className="mb-2">{plan.name}</h5>
                                <p className="text-primary fw-semibold mb-3">{formatCurrency(plan.monthly_price ?? 0)}/tháng</p>

                                <div
                                    className="text-muted mb-3"
                                    dangerouslySetInnerHTML={{
                                        __html: sanitizeHtml(plan.description || "<p>Chưa có mô tả cho gói này.</p>"),
                                    }}
                                />

                                <label htmlFor="planPrice" className="form-label">Gói tháng</label>
                                <select
                                    id="planPrice"
                                    className="form-select mb-3"
                                    value={selectedPlanPriceId ?? ""}
                                    onChange={(event) => setSelectedPlanPriceId(Number(event.target.value))}
                                >
                                    {(plan.prices && plan.prices.length > 0) ? (
                                        plan.prices.map((item) => (
                                            <option key={item.id} value={item.id}>
                                                {item.duration_in_months} tháng - {formatCurrency(Number(item.price ?? 0))}
                                            </option>
                                        ))
                                    ) : (
                                        <option value={selectedPlanPriceId ?? ""}>1 tháng - {formatCurrency(plan.monthly_price ?? 0)}</option>
                                    )}
                                </select>

                                <p className="mb-3 fw-semibold">Tổng tiền: {formatCurrency(total)}</p>

                                <button type="button" className="btn btn-primary w-100" onClick={handlePayment} disabled={isPaying}>
                                    {isPaying ? "Đang tạo link..." : "Thanh toán"}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </React.Fragment>
    );
};

export default SubscriptionDetailPage;
